<template>
  <div />
</template>

<script>

/**
 * Vue: Secondary product data.
 * -----------------------------------------------------------------------------
 * Renderless component that fetches additional product data
 *
 * @param {String} context - The context the data is being requested for.
 * @param {Number} limit - The number of products required to be displayed.
 * @param {Number} productHandles - List of product handles.
 *
 */
import { mapState } from 'vuex'
import { formatProduct, formatRecommendationsProduct } from '~/helpers/format'
import productQuery from '~/graphql/queries/product.gql'

export default {
  name: 'SecondaryProductData',

  props: {
    context: {
      type: String,
      default: 'recommendation',
      validator: (value) => ['recommendation', 'curated', 'recently-viewed', 'media-slideshow'].includes(value),
      required: true,
    },

    limit: {
      type: Number,
      default: 4,
    },

    productHandles: {
      type: Array,
      default: () => [],
    },
  },

  emits: {
    'products-updated': (payload) => Array.isArray(payload),
  },

  data() {
    return {
      products: [],
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      product: (state) => state.product.product,
    }),
  },

  watch: {

    /**
     * When the product changes, fetch new secondary product data.
     */
    product: {
      deep: true,
      immediate: true,
      handler(product) {
        this.returnProductList(product)
      },
    },

    /**
     * When products updates, emit its data to the applicable parent.
     */
    products() {
      this.$emit('products-updated', this.products)
    },
  },

  methods: {

    /**
     * Completes the applicable action based on the given context.
     * @param {Object} product - Shopify product object.
     */
    async returnProductList(product) {
      if (this.context === 'recommendation') {
        try {
          if (!product?.id) {
            this.$emit('products-updated', [])
            return
          }

          const responseProducts = await this.fetchRecommendedProducts(product.id)

          const formattedProducts = responseProducts.map(
            response => formatRecommendationsProduct(response),
          )

          this.products = formattedProducts
        } catch (error) {
          throw new Error(error)
        }
        return
      }

      if (this.context === 'recently-viewed') {
        try {
          if (!product?.id && !localStorage) {
            this.$emit('products-updated', [])
            return
          }

          const storedData = localStorage.getItem('recently-viewed-products')
          const productsViewed = storedData ? JSON.parse(storedData) : []
          let recentlyViewed = []

          if (product?.handle && product.handle !== '') {
            recentlyViewed =
              productsViewed
                .filter(item => item !== product.handle)
                .reverse()
                .slice(0, this.limit)

            if (!productsViewed.includes(product.handle)) {
              productsViewed.push(product.handle)
            }
          }

          if (productsViewed.length > this.limit + 1) {
            productsViewed.shift()
          }

          localStorage.setItem('recently-viewed-products', JSON.stringify(productsViewed))

          if (recentlyViewed.length > 0) {
            const responseProducts = await this.fetchProductsByHandles(recentlyViewed)
            const formattedProducts = responseProducts.map(
              response => formatProduct(response),
            )

            this.products = formattedProducts
            return
          }

        } catch (error) {
          throw new Error(error)
        }
        return
      }

      if (this.context === 'media-slideshow') {
        try {

          if (this.productHandles?.length) {
            const responseProducts = await this.fetchProductsByHandles(this.productHandles)
            const formattedProducts = responseProducts.map(
              response => formatProduct(response),
            )

            this.products = formattedProducts

            return
          }

        } catch (error) {
          throw new Error(error)
        }
        return
      }

      if (!product?.tags?.length) {
        this.$emit('products-updated', [])
        return
      }

      const handles = this.getHandlesFromTags(product.tags)

      if (!handles.length) {
        this.$emit('products-updated', [])
        return
      }
      const responseProducts = await this.fetchProductsByHandles(handles)
      const formattedProducts = responseProducts.map(
        response => formatProduct(response),
      )

      this.products = formattedProducts
    },

    /**
     * Returns an array of handles that have been set as recommended-products
     * in the main product tags.
     * @param {Array} tags - Product tag array.
     * @returns {Array}
     */
    getHandlesFromTags(tags) {
      if (!tags?.length) {
        return []
      }
      const handles = tags.filter(tag => tag.includes('recommended-product:'))
      return handles.map(handle => handle.split(':')[1])
    },

    /**
     * Fetch product data, one product per handle supplied.
     * @param {Array} productHandles - Array of product handles.
     * @returns {Object}
     */
    fetchProductsByHandles(productHandles) {
      return new Promise(async(resolve, reject) => {
        if (!productHandles.length) {
          resolve([])
        }

        try {
          const queue = productHandles.map((handle) => {
            return cnvs.Query({
              query: productQuery,
              variables: {
                country: cnvs.store.country,
                handle,
                language: cnvs.store.language,
              },
            })
          })

          const response = await Promise.allSettled(queue)
          const promiseTagHandle = response.map((promise) => {
            if (promise?.value?.product === null) {
              return false
            }

            return promise.value
          }).filter(Boolean)

          resolve(promiseTagHandle)
        } catch (error) {
          reject(error)
          throw new Error(error)
        }
      })
    },

    /**
     * Fetch recommended products data from Shopify Recommended Products API.
     * @param {Number} productId - Id of product to base recommended products
     * on.
     * @returns {Object}
     */
    fetchRecommendedProducts(productId) {
      const productLimit = this.limit

      return new Promise(async(resolve, reject) => {
        try {
          const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=${productLimit}`)
          const products = await response.json()

          if (products?.products?.length) {
            resolve(products.products)
            return
          }

          reject(products)
        } catch (error) {
          throw new Error(error)
        }
      }).catch((error) => {
        throw new Error(error)
      })
    },
  },
}
</script>
